import { reactive, computed, getCurrentInstance, onMounted } from "vue";
import { useEmployees } from "@/composables/employees/useEmployees";
import { usePositions } from "@/composables/positions/usePositions";
import { useFilters } from "@/composables/filters/useFilters";
import { useHighlightRow } from "@/composables/common/useHighlightRow";
import { useEmployeeStore } from "@/store/pinia/employee";
import { recalculateEmployeeReadiness, getChatterList } from "@/api";
import { updateUserAvatarInStorage } from "@components/helper/localStorageHelper";
import {
  getPositionId,
  getListOfPositionsForAssignment,
  getListOfPositionsForPrepareAssignment,
} from "@/api";

let instance = null;

export function useEmployeesOrganizationTableViewState() {
  if (instance) return instance;
  const employees = useEmployees();
  const positions = usePositions();
  const { highlight } = useHighlightRow();
  const filter = useFilters();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  const state = reactive({
    filters: {
      values: ["draft", "active"],
      searchText: "",
      searchGhostText: "Search Employee",
      showFilterDialog: false,
      key: "employeesOrganizationTable",
      clearSelection: "",
      statusOptions: ["draft", "active", "terminated"],
      filterGroups: [
        {
          label: "Access",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "No Access", value: "no_access" },
            { label: "Has Access", value: "has_access" },
          ],
        },
        {
          label: "Assignments",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "Has Assignments", value: "has_assignment" },
            { label: "No Assignments", value: "no_assignment" },
          ],
        },
        {
          label: "Archived",
          items: [
            { label: "Do not Show", value: "no_archived" },
            { label: "Show", value: "show_archived" },
          ],
        },
      ],
    },
    archiveAction: null,
    loading: {
      table: false,
      assignmentWizard: false,
      relieveAssignment: false,
      employeeAssignment: false,
    },
    assignmentWizard: {
      show: false,
      selectedPosition: null,
      isAssigning: false,
    },
    preparePosition: {
      show: false,
      selectedPosition: null,
      isAssigning: false,
    },
    relieveAssignment: {
      show: false,
      currentPosition: "",
      confirmationPending: false,
      type: "",
    },
    activeEmployeeModal: {
      show: false,
      message: "",
    },
    cannotRenamEmployeeModal: {
      show: false,
      message: "",
    },
    terminateEmployeeModal: {
      show: false,
      message: "",
    },
    suspendEmployeeAccessModal: {
      show: false,
      message: "",
    },
    giveBackEmployeeAccessModal: {
      show: false,
      message: "",
    },
    addEmployeeModal: {
      show: false,
      message: "",
    },
    changePictureModal: {
      show: false,
      avatarValue: null,
      croppedImage: null,
    },
    giveAccessModal: {
      show: false,
      dataType: "step1",
      existingUser: null,
      showEmailLoadingIcon: false,
      access_level: null,
    },
    giveBackAccessModal: {
      show: false,
    },
    reactivateEmployeeModal: {
      show: false,
      message: "",
    },
    deleteEmployeeModal: {
      show: false,
      message: "",
    },
    archiveEmployeeModal: {
      show: false,
      message: "",
    },
    openChatterModal: {
      show: false,
      chatterData: [],
      totalEmployeeChatter: 0,
      page: 1,
    },
    table: {
      selectedEmployees: [],
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
    currentRow: null,
    currentEmployee: null,
    assignmentPosition: {
      show: false,
      currentPosition: "",
    },
    paginationRoleModel: {
      page: 1,
      total: 0,
      perPage: 10,
    },
    searchMode: false,
    rolesList: [],
    responsibityList: [],
    assignmentItems: [],
    sanitizedItems: [],
    showRoleToPosition: false,
    attachmentId: null,
    unmatchedResponsibilities: [],
    showAssignmentCreationFlag: false,
    primaryPositions: {
      availablePositions: [],
      isLoadingMore: false,
      currentPage: 1,
      totalCount: 0,
      hasMoreData: true,
      currentSearchTerm: "",
    },
    preparePositions: {
      availablePositions: [],
      isLoadingMore: false,
      currentPage: 1,
      totalCount: 0,
      hasMoreData: true,
      currentSearchTerm: "",
    },
    featureFlags: {
      employeeAvailabilitySelector: true,
    },
  });

  const hasSelectedEmployees = computed(
    () => state.table.selectedEmployees.length > 0
  );

  const loadFiltersOnReload = () => {
    const savedFilters = filter.getFilters(state.filters.key);
    if (savedFilters && savedFilters.length > 0) {
      state.filters.values = savedFilters;
    } else {
      // If no saved filters, use default filters
      state.filters.values = ["draft", "active"];
    }
  };

  const applyFilters = async (filters) => {
    const newArray = filters
      .map((item) => {
        if (item.value !== null) {
          if (typeof item === "string") {
            return item;
          } else if (
            typeof item === "object" &&
            // eslint-disable-next-line no-prototype-builtins
            item.hasOwnProperty("value")
          ) {
            return item.value;
          }
        }
      })
      .filter(Boolean);
    filter.setFilters({ key: state.filters.key, filters: newArray });
    state.filters.values = newArray;
    
    // Clear existing filters and apply new ones
    employees.clearEmployeeDynamicFilters();
    employees.setPaginationDataOffset(0);
    
    // Collect status filters first
    const statusFilters = [];
    let archivedFilterApplied = false;
    
    state.filters.values.forEach((el) => {
      const lowerEl = typeof el === "string" ? el.toLowerCase() : el;
      if (
        lowerEl === "draft" ||
        lowerEl === "active" ||
        lowerEl === "terminated"
      ) {
        statusFilters.push(lowerEl);
      }
      if (lowerEl === "show" || lowerEl === "do not show" || lowerEl === "show_archived" || lowerEl === "no_archived") {
        archivedFilterApplied = true;
        if (lowerEl === "show" || lowerEl === "show_archived") {
          employees.addEmployeeFilter("archived", true);
        } else {
          employees.addEmployeeFilter("archived", false);
        }
      }
      if (lowerEl === "no access" || lowerEl === "no_access") {
        employees.addEmployeeFilter("has_access", false);
      }
      if (lowerEl === "has access" || lowerEl === "has_access") {
        employees.addEmployeeFilter("has_access", true);
      }
      if (lowerEl === "no assignments" || lowerEl === "no_assignment") {
        employees.addEmployeeFilter("has_assignment", false);
      }
      if (lowerEl === "has assignments" || lowerEl === "has_assignment") {
        employees.addEmployeeFilter("has_assignment", true);
      }
    });
    
    // Apply all status filters (they will be combined into an array automatically)
    statusFilters.forEach((status) => {
      employees.addEmployeeFilter("status", status);
    });
    
    if (!archivedFilterApplied) {
      employees.addEmployeeFilter("archived", false);
    }

    // Re-apply search filters if they exist
    if (state.filters.searchText && state.filters.searchText.trim() !== "") {
      employees.addEmployeeFilter("name", state.filters.searchText);
    }

    state.filters.showFilterDialog = false;
    
    // Load employees with new filters
    await employees.loadAllEmployees();
  };

  const handleEmployeeSort = (sortData) => {
    const finalSortField = sortData.descending
      ? `-${sortData.field}`
      : `${sortData.field}`;
    employees.setOrderingDataField(finalSortField);
    employees.loadAllEmployees();
  };

  const removeFilters = async (removedFilters) => {
    employees.clearEmployeeDynamicFilters();
    employees.setPaginationDataOffset(0);
    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedFilters;
    await applyFilters(
      state.filters.values.map((value) => ({ label: value, value: value }))
    );
  };

  const removeAllFiltersAndReload = async () => {
    employees.clearEmployeeDynamicFilters();
    employees.addEmployeeFilter("archived", false);
    employees.setPaginationDataOffset(0);

    filter.setFilters({ key: state.filters.key, filters: [] });
    state.filters.values = [];
    state.filters.searchText = "";
    state.filters.searchGhostText = "Search Employee";

    await employees.loadAllEmployees();
  };

  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const performSearch = async (string) => {
    employees.removeEmployeeFilter("first_name");
    employees.removeEmployeeFilter("last_name");
    employees.removeEmployeeFilter("name");
    if (string && string.trim() !== "") {
      employees.addEmployeeFilter("name", string);
    }
    employees.setPaginationDataOffset(0);
    state.filters.searchText = string;
    await employees.loadAllEmployees();
  };

  const debouncedSearch = debounce(performSearch, 500);

  const handleSearch = (string) => {
    const queryString = string;
    debouncedSearch(queryString);
  };

  const handleRequest = async ({ page, itemsPerPage }) => {
    let archivedFilterApplied = false;
    state.filters.values.forEach((el) => {
      if (el == "Show" || el == "Do not Show") {
        archivedFilterApplied = true;
      }
    });
    if (!archivedFilterApplied) {
      employees.addEmployeeFilter("archived", false);
    }
    employees.setPaginationDataLimit(itemsPerPage);
    employees.setPaginationDataOffset((page - 1) * itemsPerPage);
    await employees.loadAllEmployees();
  };

  const currentEmployee = computed(
    () =>
      state.assignmentWizard.currentEmployee ||
      state.relieveAssignment.currentEmployee
  );

  const getEmployeeCardProps = (employee) => {
    const isAvailabilitySelectorEnabled =
      state.featureFlags.employeeAvailabilitySelector;

    const hideAvailabilitySelector =
      !isAvailabilitySelectorEnabled || employee?.primary_position || false;

    // Try multiple possible ID fields
    const employeeId = employee?.id || employee?.employee_id || employee?.account_employee_id || null;
    
    console.log('📋 getEmployeeCardProps called', {
      employeeId: employeeId,
      employeeKeys: Object.keys(employee || {}),
      employee: employee
    });

    return {
      firstName: employee.first_name,
      lastName: employee.last_name,
      photoUrl: employee.avatar,
      availability: employee.availability,
      showAvailability: true,
      nameUrl: null, // Don't use nameUrl, we'll handle navigation via query params (drilldown)
      showLinkUnderline: false,
      hideAvailabilitySelector: hideAvailabilitySelector,
      isAllocated: employee?.primary_position || false,
      emitFunctionToComposable: true,
      employeeId: employeeId, // Pass employeeId for navigation
    };
  };

  const getEmployeeAssigmentTagProps = {
    getTypeAssignmentTag: (positionData) => {
      if (!positionData) return "not allowed";
      if (!positionData?.occupied_by) {
        return "vacant";
      } else if (positionData?.occupied_by) {
        return "occupied";
      } else if (positionData?.blocked) {
        return "blocked";
      } else {
        return "not allowed";
      }
    },
    getBasicInfo: (item) => ({
      employeeId: item.id,
      firstName: item.first_name,
      lastName: item.last_name,
      photoUrl: item.avatar,
      addHoverEffect: true,
    }),

    getPositionInfo: (item) => ({
      positionCode: item.primary_position?.code_str?.split(" ")[0] || null,
      positionName: item.primary_position?.name,
      effective_date: item.primary_position?.effective_on,
      rolesCount: item.primary_position?.roles_count,
      responsibilityCount: item.primary_position?.responsibilities_count,
      fte: item.primary_position?.allocation || null,
      employeeLoading:
        item.primary_position?.employee_loading === 1 ? false : true,
    }),

    getTypeInfo: (item) => ({
      type:
        item?.status !== "active"
          ? "not allowed"
          : item?.primary_position
          ? getEmployeeAssigmentTagProps.getTypeAssignmentTag(
              item.primary_position
            )
          : "no primary assignment",
    }),
  };

  const getEmployeePrepareAssignmentCardProps = {
    getTypeAssignmentTag: (positionData) => {
      if (!positionData) return "not allowed";
      if (positionData.occupied_by) {
        return "occupied";
      } else if (positionData.occupied_by === null) {
        return "vacant";
      } else if (positionData.status === "inactive") {
        return "inactive";
      } else {
        return "not allowed";
      }
    },
    getOccupiedByInfo: (item) => ({
      occupiedList: item?.preparing_for_positions?.[0]?.occupied_by
        ? [
            {
              firstName: item.preparing_for_positions[0].occupied_by.first_name,
              lastName: item.preparing_for_positions[0].occupied_by.last_name,
              photoUrl: item.preparing_for_positions[0].occupied_by.avatar,
              addHoverEffect: true,
            },
          ]
        : [],
    }),
    getEmployeeInfo: (item) => ({
      firstName: item.first_name,
      lastName: item.last_name,
      photoUrl: item.avatar,
      addHoverEffect: true,
      nameUrl: `employees/${item.id}/pathtoreadiness`,
    }),

    getPositionInfo: (item) => ({
      positionCode:
        item.preparing_for_positions?.[0]?.code_str?.split(" ")[0] || null,
      positionName: item.preparing_for_positions?.[0]?.name,
      effective_date: item.preparing_for_positions?.[0]?.effective_on,
      fte:
        item.preparing_for_positions?.length > 0
          ? item.preparing_for_positions[0].allocation
          : null,
      roles: item.preparing_for_positions?.[0]?.roles_count || 0,
      responsibilities:
        item.preparing_for_positions?.[0]?.responsibilities_count || 0,
      employeeLoading:
        item.preparing_for_positions?.[0]?.employee_loading === 0
          ? false
          : true,
    }),
    getTypeInfo: (item) => ({
      type:
        item?.status !== "active"
          ? "not allowed"
          : item?.preparing_for_positions?.length > 0
          ? getEmployeePrepareAssignmentCardProps.getTypeAssignmentTag(
              item.preparing_for_positions[0]
            )
          : "no prepare assignment",
    }),
  };

  const getStateBadgeProps = (employee) => {
    return {
      access: getEmployeeAccess(employee?.access),
      status: employee.status,
      archived: employee?.archived || false,
      user: employee?.user || null,
      invitedDate: employee?.user?.date_of_invite || employee?.date_of_invite || null,
    };
  };

  const getEmployeeAccess = (access) => {
    if (access?.is_suspended) {
      return "suspended";
    } else {
      return access ? access.employee_status : "no-access";
    }
  };

  const checkUserEmail = async ({ accountId, email }) => {
    state.giveAccessModal.showEmailLoadingIcon = true;
    const response = await employees.checkEmailUser({ accountId, email });
    state.giveAccessModal.showEmailLoadingIcon = false;
    if (response.blocked) {
      state.giveAccessModal.dataType = "emailBlocked";
    } else if (response.employee) {
      state.giveAccessModal.dataType = "emailUsed";
      state.giveAccessModal.existingUser = response.used_by;
    } else if (response.access && response.user && !response.employee) {
      const employeeName = (
        employees.getCurrentEmployee()?.first_name +
        employees.getCurrentEmployee()?.last_name
      )
        .toLowerCase()
        .replace(/\s/g, "");

      if (response.used_by?.toLowerCase().replace(/\s/g, "") === employeeName) {
        state.giveAccessModal.dataType = "details_email_user";
      } else {
        state.giveAccessModal.dataType = "accessedEmail";
      }
      state.giveAccessModal.existingUser = response.used_by;
      if (response.access_level === "user") {
        state.giveAccessModal.access_level = "app-User";
      } else if (response.access_level === "co-owner") {
        state.giveAccessModal.access_level = "co-Owner";
      } else {
        state.giveAccessModal.access_level = response.access_level;
      }
    } else {
      state.giveAccessModal.dataType = "step2";
    }
  };

  const giveAccessToEmployee = async ({ accountId, payload }) => {
    proxy.$loader.setAppLoading(true);

    try {
      await employees.giveAccessToEmployee({ accountId, payload });
      await employees.loadAllEmployees();
      proxy.$loader.setAppLoading(false);
      state.giveAccessModal.show = false;
      state.giveBackAccessModal.show = false;
    } catch (error) {
      proxy.$loader.setAppLoading(false);

      let errorMessage =
        "An error occurred while giving access to the employee.";

      if (error.response?.data) {
        const errorData = error.response.data;

        if (errorData.account_permission?.non_field_errors) {
          errorMessage =
            "You can't give access to the owner's email. Only the account owner can do that.";
        }
      }

      proxy.$toast.error(errorMessage);
    }
  };

  const openAssignmentWizard = () => {
    getListOfPrimaryPositions();
    state.assignmentWizard.show = true;
    state.assignmentWizard.selectedPosition = null;
  };

  const openAssignmentPosition = async (position, accountID) => {
    // Position details modal logic
  };

  const openPreparePositionDialog = async (position, accountID) => {
    try {
      proxy.$loader.show();
      state.assignmentPosition.show = true;

      // Handle both primary positions and prepare positions
      // Note: 'position' parameter is actually the employee object
      let positionData = null;
      let positionId = null;

      // Check if it's a prepare position
      if (position?.preparing_for_positions && position.preparing_for_positions.length > 0) {
        positionData = position.preparing_for_positions[0];
        // For prepare positions, the position ID is typically in the 'position' field
        // This is the position assignment, and 'position' field contains the actual position ID
        positionId = positionData?.position;
      }
      // Check if it's a primary position (object)
      else if (position?.primary_position) {
        positionData = position.primary_position;
        // For primary position assignments, the structure might vary:
        // - If it's an assignment object, 'position' field contains the position ID
        // - If it's already a position object, 'id' field contains the position ID
        // - Sometimes it might be 'position_id'
        positionId = positionData?.position || positionData?.id || positionData?.position_id;
        
        // If positionId is still an object, try to extract ID from it
        if (positionId && typeof positionId === 'object') {
          positionId = positionId?.id || positionId?.position;
        }
      }
      // Check if it's primary_positions array
      else if (position?.primary_positions && position.primary_positions.length > 0) {
        positionData = position.primary_positions[0];
        // Same logic as primary_position above
        positionId = positionData?.position || positionData?.id || positionData?.position_id;
        
        // If positionId is still an object, try to extract ID from it
        if (positionId && typeof positionId === 'object') {
          positionId = positionId?.id || positionId?.position;
        }
      }
      
      if (!positionData) {
        proxy.$loader.hide();
        proxy.$toast.error("Position data is not available.");
        state.assignmentPosition.show = false;
        return;
      }

      if (!positionId) {
        // Log the structure to help debug
        console.error("Position ID not found. Position data structure:", positionData);
        proxy.$loader.hide();
        proxy.$toast.error("Position ID is missing. Cannot load position details.");
        state.assignmentPosition.show = false;
        return;
      }

      // Ensure positionId is a number/string, not an object
      if (typeof positionId === 'object') {
        console.error("Position ID is an object, not a primitive:", positionId);
        positionId = positionId?.id || positionId?.position;
      }

      if (!positionId) {
        proxy.$loader.hide();
        proxy.$toast.error("Invalid position ID format.");
        state.assignmentPosition.show = false;
        return;
      }

      const fullPositionData = await getPositionId(
        accountID,
        positionId
      );

      if (!fullPositionData?.data) {
        proxy.$loader.hide();
        proxy.$toast.error("Failed to load position details.");
        state.assignmentPosition.show = false;
        return;
      }

      state.assignmentPosition.currentPosition = {
        ...positionData,
        ...fullPositionData.data,
        attachments: fullPositionData.data?.attachments || [],
      };

      state.rolesList = await positions.getRolesById({ accountId: accountID });
      state.responsibityList = await positions.getResponsibilityByAttachRoles({
        accountId: accountID,
      });
      state.sanitizedItems = getSanitizedItems();
      state.assignmentItems = paginatedAttachedRoles(state.sanitizedItems, 1);
      proxy.$loader.hide();
    } catch (error) {
      proxy.$loader.hide();
      state.assignmentPosition.show = false;
      
      if (error.response?.status === 404) {
        proxy.$toast.error("Position not found. It may have been deleted or you don't have access to it.");
      } else {
        proxy.$toast.error("An error occurred while loading position details.");
      }
      console.error("Error in openPreparePositionDialog:", error);
    }
  };

  const getSanitizedItems = () => {
    // Implementation from original
    return [];
  };

  const paginatedAttachedRoles = (sanitizedItems, page) => {
    // Implementation from original
    return [];
  };

  const openPreparePosition = () => {
    getListOfPreparePositions();
    state.preparePosition.show = true;
    state.preparePosition.selectedPosition = null;
  };

  const closeAssignmentWizard = () => {
    state.assignmentWizard.show = false;
    state.assignmentWizard.selectedPosition = null;
  };

  const closePreparePosition = () => {
    state.preparePosition.show = false;
    state.preparePosition.selectedPosition = null;
  };

  const closeRelieveAssignment = () => {
    state.relieveAssignment.show = false;
    state.relieveAssignment.currentPosition = null;
    employees.setCurrentEmployee(null);
    state.relieveAssignment.confirmationPending = false;
  };

  const relievePrimaryPositionFromEmployee = async (accountID, data) => {
    proxy.$loader.setAppLoading(true);
    const employee_id = employees.getCurrentEmployee()?.id;
    const payload = {
      effective_by: data,
    };
    await employees.removePositionAssignmentToEmployee({
      accountId: accountID,
      assignment_id: state.relieveAssignment.currentPosition.id,
      employee_id: employee_id,
      data: payload,
    });
    await employees.loadAllEmployees();
    await positions.loadAllPositions();
    await recalculateEmployeeReadiness(accountID, employee_id);
    state.relieveAssignment.show = false;
    employees.setCurrentEmployee(null);
    proxy.$loader.setAppLoading(false);
  };

  const handleEmployeeDropdownOperation = async (operation, employee) => {
    state.currentRow = employee;
    if (!operation || !employee) return;
    employees.setCurrentEmployee(employee);
    switch (operation.value) {
      case "activate_action":
        state.activeEmployeeModal.message = `${
          employees.getCurrentEmployee()?.first_name || ""
        } ${
          employees.getCurrentEmployee()?.last_name || ""
        } is about to be terminated. We will relieve the employee of all their assignments. We will also suspend access for the employee to this account.`;
        state.activeEmployeeModal.show = true;
        break;
      case "delete_action":
        state.deleteEmployeeModal.message = `${
          employees.getCurrentEmployee()?.first_name || ""
        } ${
          employees.getCurrentEmployee().last_name || ""
        } is about to be Deleted. This action is irreversible.`;
        state.deleteEmployeeModal.show = true;
        break;
      case "edit_action":
        if (employee.access != null) {
          state.cannotRenamEmployeeModal.show = true;
        } else {
          state.addEmployeeModal.show = true;
        }
        break;
      case "change_picture_action":
        state.changePictureModal.show = true;
        break;
      case "give_access_action":
        state.giveAccessModal.show = true;
        break;
      case "suspend_access_action":
        state.suspendEmployeeAccessModal.show = true;
        state.suspendEmployeeAccessModal.message = `You are about to suspend access for ${
          employees.getCurrentEmployee()?.first_name || ""
        } ${employees.getCurrentEmployee()?.last_name || ""} .`;
        break;
      case "give_back_access_action":
        state.giveBackAccessModal.show = true;
        break;
      case "terminate_action":
        state.terminateEmployeeModal.message = `${
          employees.getCurrentEmployee()?.first_name || ""
        } ${
          employees.getCurrentEmployee()?.last_name || ""
        } is about to be terminated. We will relieve the employee of all their assignments. We will also suspend access for the employee to this account.`;
        state.terminateEmployeeModal.show = true;
        break;
      case "reactivate_action":
        state.reactivateEmployeeModal.show = true;
        break;
      case "archive_action":
        state.archiveAction = "archive";
        state.archiveEmployeeModal.show = true;
        break;
      case "unarchive_action":
        state.archiveAction = "unarchive";
        state.archiveEmployeeModal.show = true;
        break;
      default:
        break;
    }
  };

  const handleEmployeeOperation = async (
    operation,
    accountId,
    payload = null
  ) => {
    try {
      proxy.$loader.setAppLoading(true);
      if (operation == employees.operations.TERMINATE_EMPLOYEE) {
        await employees.terminate({
          accountId: accountId,
          employee: employees.getCurrentEmployee(),
          data: payload,
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
        await recalculateEmployeeReadiness(
          accountId,
          employees.getCurrentEmployee()?.id
        );
        state.terminateEmployeeModal.show = false;
      } else if (operation == employees.operations.ACTIVATE_EMPLOYEE) {
        await employees.activate({
          accountId,
          employee: employees.getCurrentEmployee(),
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
        state.activeEmployeeModal.show = false;
      } else if (operation == employees.operations.DELETE_EMPLOYEE) {
        await employees.deleteEmp({
          accountId,
          employee: employees.getCurrentEmployee(),
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
        state.deleteEmployeeModal.show = false;
      } else if (operation == employees.operations.REACTIVATE_EMPLOYEE) {
        await employees.reactivate({
          accountId,
          employee: employees.getCurrentEmployee(),
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
        state.reactivateEmployeeModal.show = false;
      } else if (operation == employees.operations.EDIT_EMPLOYEE) {
        if (!payload) return;
        const newEmployee = await employees.saveEmployee({
          accountId,
          employee: payload,
          allData: employees.getCurrentEmployee(),
        });
        if (!payload?.id) {
          if (newEmployee) {
            const { data } = newEmployee;
            
            // Navigate to employee detail page using query parameters to show in drilldown mode
            if (data?.id) {
              state.addEmployeeModal.show = false;
              proxy.$loader.setAppLoading(false);
              
              // Wait for modal to close and DOM to update
              await proxy.$nextTick();
              
              // Navigate first
              await proxy.$router.push({
                name: 'NewOrganization',
                params: { tab: 'employees' },
                query: {
                  employeeId: String(data.id),
                  employeeTab: 'pathtoreadiness'
                }
              }).catch(() => {});
              
              // Return employee data so component can emit drilldown event
              return { createdEmployee: data };
            }
            state.addEmployeeModal.show = false;
            const existingFilters = [...state.filters.values];
            state.filters.searchText = `${data.first_name} ${data.last_name}`;

            employees.clearEmployeeDynamicFilters();
            employees.setPaginationDataOffset(0);

            let archivedFilterApplied = false;
            existingFilters.forEach((el) => {
              if (el == "draft" || el == "active" || el == "terminated") {
                employees.addEmployeeFilter("status", el);
              }
              if (el == "Show" || el == "Do not Show") {
                archivedFilterApplied = true;
                if (el == "Show") {
                  employees.addEmployeeFilter("archived", true);
                } else {
                  employees.addEmployeeFilter("archived", false);
                }
              }
            });
            if (!archivedFilterApplied) {
              employees.addEmployeeFilter("archived", false);
            }

            const stringToArray = state.filters.searchText.split(" ");
            if (stringToArray.length == 2) {
              employees.addEmployeeFilter("first_name", stringToArray[0]);
              employees.addEmployeeFilter("last_name", stringToArray[1]);
            } else {
              employees.addEmployeeFilter("first_name", stringToArray[0]);
            }

            await employees.loadAllEmployees();
            setTimeout(() => {
              highlight({ id: newEmployee.data.id, type: "Employees" });
            }, 500);
          }
          state.addEmployeeModal.show = false;
        } else {
          await employees.loadAllEmployees();
          state.addEmployeeModal.show = false;
        }
      } else if (operation == employees.operations.CHANGE_EMPLOYEE_PICTURE) {
        const newEmployee = {
          id: employees.getCurrentEmployee()?.id,
          first_name: employees.getCurrentEmployee()?.first_name,
          last_name: employees.getCurrentEmployee()?.last_name,
          title: employees.getCurrentEmployee()?.title,
          avatar: payload,
        };
        const response = await employees.saveEmployeeWithAvatar({
          accountId,
          employee: newEmployee,
        });

        const currentEmp = employees.getCurrentEmployee();
        if (currentEmp && response?.data) {
          const updatedEmployee = {
            ...currentEmp,
            ...response.data,
          };
          employees.setCurrentEmployee(updatedEmployee);
        }

        if (response?.data && response?.data?.avatar !== undefined) {
          updateUserAvatarInStorage(response.data, response.data.avatar || "");
        }

        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
      } else if (operation == employees.operations.ARCHIVE_EMPLOYEE) {
        if (state.archiveAction === "archive") {
          await employees.archiveEmp({
            accountId: accountId,
            employee: employees.getCurrentEmployee(),
          });
        } else if (state.archiveAction === "unarchive") {
          await employees.unArchiveEmp({
            accountId: accountId,
            employee: employees.getCurrentEmployee(),
          });
        }
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
        state.archiveEmployeeModal.show = false;
      }
      if (operation != employees.operations.CHANGE_EMPLOYEE_PICTURE) {
        employees.setCurrentEmployee(null);
      }
    } catch (e) {
      console.error("error on employee operation: ", e);
    } finally {
      proxy.$loader.setAppLoading(false);
    }
  };

  const clearSearch = () => {
    state.filters.searchText = "";
    employees.removeEmployeeFilter("first_name");
    employees.removeEmployeeFilter("last_name");
    employees.removeEmployeeFilter("name");
  };

  const getEmployeeChatterData = async () => {
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      const currentEmployee = employees.getCurrentEmployee();
      if (!currentEmployee) return;
      
      state.openChatterModal.show = true;
      const response = await getChatterList(
        accountID,
        currentEmployee.id,
        "employee",
        state.openChatterModal.page
      );
      if (response && response.data) {
        let chatterItems = response.data.results.map((el) => {
          const date = new Date(el.recorded_on);
          const milliseconds = date.getTime();
          const millisecondsString = milliseconds.toString();
          let nameParts = el.recorded_by_name.split(" ");
          const newEl = {};
          newEl.id = el.id;
          if (nameParts.length === 2) {
            newEl.avatar = {
              picture: "",
              firstName: nameParts[0],
              lastName: nameParts[1],
            };
          } else {
            newEl.avatar = {
              picture: "",
              firstName: el.recorded_by_name,
              lastName: "",
            };
          }
          newEl.IsExIQtiveBot = false;
          newEl.message = el.note;
          newEl.created_on = millisecondsString;
          newEl.IsHistory = el.is_history;
          return newEl;
        });
        state.openChatterModal.chatterData.push(...chatterItems);
        state.openChatterModal.totalEmployeeChatter = response.data.count;
      }
    } catch (error) {
      console.log("e: ", error);
    }
  };

  const openEmployeeChatterModal = async (employee) => {
    if (employee) {
      employees.setCurrentEmployee(employee);
    }
    state.openChatterModal.chatterData = [];
    state.openChatterModal.page = 1;
    await getEmployeeChatterData();
  };

  const closeEmployeeChatterModal = () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.totalEmployeeChatter = 0;
    state.openChatterModal.page = 1;
    state.openChatterModal.show = false;
  };

  const requestMoreChatter = () => {
    const page = Math.ceil(state.openChatterModal.chatterData.length / 10);
    state.openChatterModal.page = page + 1;
    getEmployeeChatterData();
  };

  const onApplyDatePrepareFor = async (date) => {
    proxy.$loader.setAppLoading(true);
    try {
      await employees.onApplyDatePrepareFor({
        employee: employees.getCurrentEmployee(),
        effective_date: date,
      });
      const count = employees.allEmployees.value.length;
      if (count == 1) {
        employees.setPaginationDataOffset(0);
      }
      await employees.loadAllEmployees();
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.preparePosition.show = false;
      proxy.$loader.setAppLoading(false);
    }
  };

  const onApplyDatePrimaryAssignment = async (date) => {
    proxy.$loader.setAppLoading(true);
    try {
      await employees.onApplyDatePrimaryAssignment({
        employee: employees.getCurrentEmployee(),
        effective_date: date,
      });
      const count = employees.allEmployees.value.length;
      if (count == 1) {
        employees.setPaginationDataOffset(0);
      }
      await employees.loadAllEmployees();
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.preparePosition.show = false;
      proxy.$loader.setAppLoading(false);
    }
  };

  const getListOfPrimaryPositions = async (
    page = 1,
    searchTerm = "",
    isLoadMore = false
  ) => {
    const currentEmployee = employees.getCurrentEmployee();
    const limit = 10;
    const offset = (page - 1) * limit;
    const name = searchTerm || "";

    try {
      if (isLoadMore) {
        state.primaryPositions.isLoadingMore = true;
      }
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

      const primaryPositions = await getListOfPositionsForAssignment({
        accountId,
        limit,
        offset,
        name,
      });
      let newItems = primaryPositions.data.results.map((item) => ({
        id: item.id,
        code: item.code_str.match(/\d+/)[0],
        code_str: item.code_str,
        name: item.name,
        status: item.status,
      }));

      if (currentEmployee?.preparing_for_positions?.length > 0) {
        const preparingCodeStr =
          currentEmployee.preparing_for_positions[0].code_str;
        newItems = newItems.filter(
          (item) => item.code_str !== preparingCodeStr
        );
      }

      if (isLoadMore && state.primaryPositions.availablePositions) {
        state.primaryPositions.availablePositions = [
          ...state.primaryPositions.availablePositions,
          ...newItems,
        ];
      } else {
        state.primaryPositions.availablePositions = newItems;
      }

      state.primaryPositions.currentPage = page;
      state.primaryPositions.totalCount = primaryPositions.data.count || 0;
      state.primaryPositions.hasMoreData =
        page * limit < state.primaryPositions.totalCount;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      if (isLoadMore) {
        state.primaryPositions.isLoadingMore = false;
      }
    }
  };

  const loadMorePrimaryPositions = async () => {
    if (
      state.primaryPositions.isLoadingMore ||
      !state.primaryPositions.hasMoreData
    ) {
      return;
    }

    const nextPage = state.primaryPositions.currentPage + 1;
    await getListOfPrimaryPositions(
      nextPage,
      state.primaryPositions.currentSearchTerm,
      true
    );
  };

  const searchPrimaryPositions = async (searchTerm) => {
    state.primaryPositions.currentSearchTerm = searchTerm;
    state.primaryPositions.currentPage = 1;
    state.primaryPositions.hasMoreData = true;
    await getListOfPrimaryPositions(
      state.primaryPositions.currentPage,
      state.primaryPositions.currentSearchTerm,
      false
    );
  };

  const getListOfPreparePositions = async (
    page = 1,
    searchTerm = "",
    isLoadMore = false
  ) => {
    const currentEmployee = employees.getCurrentEmployee();
    const limit = 10;
    const offset = (page - 1) * limit;
    const name = searchTerm || "";

    try {
      if (isLoadMore) {
        state.preparePositions.isLoadingMore = true;
      }
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

      const preparePositions = await getListOfPositionsForPrepareAssignment({
        accountId,
        limit,
        offset,
        name,
      });

      let newItems = preparePositions.data.results.map((item) => ({
        ...item,
      }));

      if (
        currentEmployee &&
        currentEmployee?.primary_position &&
        currentEmployee?.primary_position?.code_str
      ) {
        const primaryCodeStr = currentEmployee?.primary_position?.code_str;
        newItems = newItems.filter((item) => item.code_str !== primaryCodeStr);
      }

      if (isLoadMore && state.preparePositions.availablePositions) {
        state.preparePositions.availablePositions = [
          ...state.preparePositions.availablePositions,
          ...newItems,
        ];
      } else {
        state.preparePositions.availablePositions = newItems;
      }

      state.preparePositions.currentPage = page;
      state.preparePositions.totalCount = preparePositions.data.count || 0;
      state.preparePositions.hasMoreData =
        page * limit < state.preparePositions.totalCount;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      if (isLoadMore) {
        state.preparePositions.isLoadingMore = false;
      }
    }
  };

  const loadMorePreparePositions = async () => {
    if (
      state.preparePositions.isLoadingMore ||
      !state.preparePositions.hasMoreData
    ) {
      return;
    }

    const nextPage = state.preparePositions.currentPage + 1;
    await getListOfPreparePositions(
      nextPage,
      state.preparePositions.currentSearchTerm,
      true
    );
  };

  const searchPreparePositions = async (searchTerm) => {
    state.preparePositions.currentSearchTerm = searchTerm;
    state.preparePositions.currentPage = 1;
    state.preparePositions.hasMoreData = true;
    await getListOfPreparePositions(
      state.preparePositions.currentPage,
      state.preparePositions.currentSearchTerm,
      false
    );
  };

  const onAssignPositionToEmployee = async ({ position, accountId }) => {
    proxy.$loader.setAppLoading(true);
    state.assignmentWizard.isAssigning = true;
    if (
      !employees.getCurrentEmployee()?.id ||
      !position?.id ||
      !employees.getCurrentEmployee()?.availability
    ) {
      return;
    }
    try {
      const currentEmployee = employees.getCurrentEmployee();
      if (currentEmployee && state.showAssignmentCreationFlag) {
        const positionWithCodeStr = {
          code_str: position.code,
          name: position.name,
          effective_on: position.effective_on,
          roles_count: null,
          responsibilities_count: null,
          allocation: position.allocation || 1,
          employee_loading: 0,
          status: "active",
          occupied_by: {
            first_name: currentEmployee.first_name,
            last_name: currentEmployee.last_name,
            avatar: currentEmployee.avatar,
          },
        };

        const updatedEmployee = {
          ...currentEmployee,
          primary_position: positionWithCodeStr,
        };

        employees.setCurrentEmployee(updatedEmployee);

        const allEmployees = [...employees.allEmployees.value];
        const employeeIndex = allEmployees.findIndex(
          (emp) => emp.id == currentEmployee.id
        );

        if (employeeIndex !== -1) {
          allEmployees[employeeIndex] = updatedEmployee;

          const employeeStore = useEmployeeStore();
          employeeStore.setEmployees(allEmployees);
        }
      }
      if (state.showAssignmentCreationFlag) {
        state.assignmentWizard.show = false;
      }
      await employees.onAssignPositionToEmployee({
        employee: employees.getCurrentEmployee(),
        position,
        accountID: accountId,
      });
      await new Promise((resolve) => setTimeout(resolve, 3000));
      await employees.loadAllEmployees(false);
      await recalculateEmployeeReadiness(
        accountId,
        employees.getCurrentEmployee()?.id
      );
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.assignmentWizard.show = false;
      state.assignmentWizard.isAssigning = false;
    }
    proxy.$loader.setAppLoading(false);
  };

  const onAssignPreparePositionToEmployee = async ({ obj, accountId }) => {
    proxy.$loader.setAppLoading(true);
    state.preparePosition.isAssigning = true;
    if (
      !employees.getCurrentEmployee()?.id ||
      !obj?.position?.id ||
      !employees.getCurrentEmployee()?.availability
    ) {
      return;
    }
    try {
      const currentEmployee = employees.getCurrentEmployee();
      if (currentEmployee && state.showAssignmentCreationFlag) {
        const positionWithCodeStr = {
          ...obj.position,
          code: obj.position.code_str,
          occupied_by: currentEmployee,
          status: "active",
          effective_on: obj.effective_date,
          allocation: obj.position.allocation || 1,
          roles_count: null,
          responsibilities_count: null,
          employee_loading: 1,
        };

        const updatedEmployee = {
          ...currentEmployee,
          preparing_for_positions: [positionWithCodeStr],
        };

        employees.setCurrentEmployee(updatedEmployee);

        const allEmployees = [...employees.allEmployees.value];
        const employeeIndex = allEmployees.findIndex(
          (emp) => emp.id == currentEmployee.id
        );

        if (employeeIndex !== -1) {
          allEmployees[employeeIndex] = updatedEmployee;
          const employeeStore = useEmployeeStore();
          employeeStore.setEmployees(allEmployees);
        }
      }
      if (state.showAssignmentCreationFlag) {
        state.preparePosition.show = false;
      }
      await employees.onAssignPreparePositionToEmployee({
        employee: employees.getCurrentEmployee(),
        position: obj.position,
        effective_date: obj.effective_date,
        accountID: accountId,
        isNotify: obj.isNotify,
      });
      await new Promise((resolve) => setTimeout(resolve, 3000));
      await employees.loadAllEmployees(false);
      await recalculateEmployeeReadiness(
        accountId,
        employees.getCurrentEmployee()?.id
      );
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.preparePosition.show = false;
      state.preparePosition.isAssigning = false;
    }
    proxy.$loader.setAppLoading(false);
  };

  const initializeFilters = async () => {
    const app = getCurrentInstance();
    const { proxy } = app || {};

    employees.clearEmployeeDynamicFilters();

    if (!proxy) {
      return;
    }

    try {
      const hideArchivedFilterEmployees = await proxy.$getFlag(
        "hide_archived_filter_employees",
        false
      );

      if (hideArchivedFilterEmployees) {
        state.filters.filterGroups = state.filters.filterGroups.filter(
          (group) => group.label !== "Archived"
        );
      }

      const employeeAvailabilitySelector = await proxy.$getFlag(
        "employee_availability_selector",
        true
      );
      state.featureFlags.employeeAvailabilitySelector =
        employeeAvailabilitySelector;
    } catch (e) {
      console.error("Error in initializeFilters:", e);
    }
  };

  instance = {
    state,
    hasSelectedEmployees,
    currentEmployee,
    openAssignmentWizard,
    openAssignmentPosition,
    openPreparePositionDialog,
    openPreparePosition,
    closeAssignmentWizard,
    closePreparePosition,
    closeRelieveAssignment,
    updateFilter: () => {},
    clearFilters: () => {},
    setLoading: () => {},
    getEmployeeCardProps,
    getEmployeeAssigmentTagProps,
    relievePrimaryPositionFromEmployee,
    getStateBadgeProps,
    handleEmployeeDropdownOperation,
    handleEmployeeOperation,
    checkUserEmail,
    applyFilters,
    removeFilters,
    giveAccessToEmployee,
    onAssignPositionToEmployee,
    onAssignPreparePositionToEmployee,
    clearSearch,
    getEmployeePrepareAssignmentCardProps,
    getSanitizedItems,
    paginatedAttachedRoles,
    openEmployeeChatterModal,
    closeEmployeeChatterModal,
    requestMoreChatter,
    onApplyDatePrepareFor,
    onApplyDatePrimaryAssignment,
    getEmployeeAccess,
    handleEmployeeSort,
    handleSearch,
    performSearch,
    handleRequest,
    getListOfPrimaryPositions,
    loadMorePrimaryPositions,
    searchPrimaryPositions,
    getListOfPreparePositions,
    loadMorePreparePositions,
    searchPreparePositions,
    removeAllFiltersAndReload,
    initializeFilters,
    loadFiltersOnReload,
  };
  return instance;
}

export function resetEmployeesOrganizationTableViewState() {
  instance = null;
}

